import { Routes } from '@angular/router';
import { LoginComponent } from './components/auth/login/login.component';
import { SamplePagesComponent } from './components/sample-pages/sample-pages/sample-pages.component';

import { FileManagementComponent } from './components/file-management/file-management.component';
import { MyProfileComponent } from './components/my-account/my-profile/my-profile.component';
import { ChangePasswordComponent } from './components/my-account/change-password/change-password.component';
import { ForgotPasswordComponent } from './components/auth/forgot-password/forgot-password.component';
import { InstrumentRunComponent } from './components/instrument-run/instrument-run.component';
import { SvTestPannelComponent } from './components/sv-test-panel/sv-test-panel.component';
import { HeaderComponent } from './components/header/header.component';
import { SignupComponent } from './components/auth/signup/signup.component';
import { UserManagementComponent } from './components/user-management/container/user-management.component';
import { OrgManagementComponent } from './components/org-management/org-management.component';
import { roleAuthGuard } from './auth-guards/role-auth.guard';
import { SampleTestpanelComponent } from './components/sample-testpanel/sample-testpanel.component';
import { tokenGuard } from './auth-guards/token/token.guard';
import { ApplicationManagementComponent } from './components/application-management/application-management.component';
import { HomeComponent } from './home/home.component';
import { AnalysisComponent } from './components/analysis/analysis.component';
import { ReportsComponent } from './components/reports/reports.component';
import { HceComponent } from './components/hce/hce.component';
import { HceAlignmentViewComponent } from './components/hce/hce-alignment-view/hce-alignment-view.component';
import { HceAssembleViewComponent } from './components/hce/hce-assemble-view/hce-assemble-view.component';
import { LicenseAgreementComponent } from './components/license-agreement/license-agreement.component';

export const routes: Routes = [
  { path: 'login', component: LoginComponent },
  { path: 'license-agreement', component: LicenseAgreementComponent },
  { path: 'user', component: MyProfileComponent, canActivate: [tokenGuard] },
  {
    path: 'change-password',
    component: ChangePasswordComponent,
    canActivate: [tokenGuard],
  },
  { path: '', redirectTo: '/home', pathMatch: 'full' },
  {
    path: 'home',
    component: HomeComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', 'Manager', 'User'] },
  },
  {
    path: 'file-management',
    component: FileManagementComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', 'Manager', 'User'] },
  },
  {
    path: 'org',
    component: OrgManagementComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['SuperAdmin'] },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./components/org-management/org-list/org-list.component').then((c) => c.OrgListComponent),
      },
      {
        path: 'quotas',
        loadComponent: () =>
          import('./components/org-management/quota-management/quota-management.component').then(
            (c) => c.QuotaManagementComponent
          ),
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'applications',
    component: ApplicationManagementComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['SuperAdmin'] },
  },
  {
    path: 'reports',
    component: ReportsComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['SuperAdmin'] },
  },
  {
    path: 'user-management',
    component: UserManagementComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', 'Manager'] },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./components/user-management/managed-user/managed-user.component').then(
            (c) => c.ManagedUserComponent
          ),
      },
      {
        path: 'pending-approvals',
        loadComponent: () =>
          import('./components/user-management/pending-approvals/pending-approvals.component').then(
            (c) => c.PendingApprovalsComponent
          ),
        pathMatch: 'full',
      },
      {
        path: 'group-management',
        loadComponent: () =>
          import('./components/user-management/group-manager/group-manager.component').then(
            (c) => c.GroupManagerComponent
          ),
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'newsfeed',
    loadComponent: () =>
      import('./components/news-management/news-management.component').then((c) => c.NewsManagementComponent),
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['SuperAdmin'] },
  },
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: 'signup', component: SignupComponent },
  { path: 'header', component: HeaderComponent },
  {
    path: 'instrument-run',
    component: InstrumentRunComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', 'Manager', 'User'] },
  },
  {
    path: 'analysis',
    pathMatch: 'full',
    component: AnalysisComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', 'Manager', 'User'] },
  },
  {
    path: 'analysis/hce/:analysisId',
    component: HceComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', 'Manager', 'User'] },
  },
  {
    path: 'analysis/hce/alignment/view/:enc',
    component: HceAlignmentViewComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', 'Manager', 'User'] },
  },
  {
    path: 'analysis/hce/assemble/view/:enc',
    component: HceAssembleViewComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', 'Manager', 'User'] },
  },
  {
    path: 'analysis/:analysisId',
    pathMatch: 'full',
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', 'Manager', 'User'] },
    loadComponent: () =>
      import('./components/analysis-report/analysis-report.component').then((c) => c.AnalysisReportComponent),
  },
  {
    path: 'plugin-viewer/:pluginId',
    pathMatch: 'full',
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', 'Manager', 'User'] },
    loadComponent: () =>
      import('./components/plugin-viewer/plugin-viewer.component').then((c) => c.PluginViewerComponent),
  },
  {
    path: 'sample-test',
    component: SampleTestpanelComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', , 'Manager', 'User'] },
  },
  {
    path: 'sample-pages',
    component: SamplePagesComponent,
    children: [
      {
        path: 'buttons',
        loadComponent: () =>
          import('./components/sample-pages/sample-pages-buttons/sample-pages-buttons.component').then(
            (c) => c.SamplePagesButtonsComponent
          ),
      },
      {
        path: 'menu',
        loadComponent: () =>
          import('./components/sample-pages/sample-pages-menu/sample-pages-menu.component').then(
            (c) => c.SamplePagesMenuComponent
          ),
      },
      {
        path: 'icons',
        loadComponent: () =>
          import('./components/sample-pages/sample-pages-icons/sample-pages-icons.component').then(
            (c) => c.SamplePagesIconsComponent
          ),
      },
      {
        path: 'input',
        loadComponent: () =>
          import('./components/sample-pages/sample-pages-input/sample-pages-input.component').then(
            (c) => c.SamplePagesInputComponent
          ),
      },
      {
        path: 'plugin',
        loadComponent: () =>
          import('./components/sample-pages/sample-plugin/sample-plugin.component').then(
            (c) => c.SamplePluginComponent
          ),
      },
    ],
  },
  {
    path: 'sv-test-panel',
    component: SvTestPannelComponent,
    canActivate: [tokenGuard, roleAuthGuard],
    data: { roles: ['Admin', , 'Manager', 'User'] },
  },
  { path: '**', redirectTo: '/home' },
];
